import React, { useState, useEffect, Suspense, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment, ContactShadows } from '@react-three/drei';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { useLoader } from '@react-three/fiber';
import * as THREE from 'three';
import './App.css';

// Component to load and display the STL
const GearModel = ({
  url,
  rotation = [0, 0, 0],
  position = [0, 0, 0],
  animate = false,
  speed = 1,
  color = "#64748b",
  centerGeometry = true,
  meshRefExternal = null,
  onGeometryReady = null
}) => {
  const geometry = useLoader(STLLoader, url);
  const internalRef = useRef();
  const meshRef = meshRefExternal || internalRef;
  const boundsReported = useRef(false);

  useEffect(() => {
    boundsReported.current = false;
  }, [geometry]);

  useEffect(() => {
    if (!geometry || boundsReported.current || !geometry.isBufferGeometry) return;
    geometry.computeBoundingBox();
    const boundingBox = geometry.boundingBox.clone();
    if (centerGeometry) {
      geometry.center();
    }
    if (boundingBox && onGeometryReady) {
      onGeometryReady({
        minX: boundingBox.min.x,
        maxX: boundingBox.max.x
      });
    }
    boundsReported.current = true;
  }, [geometry, centerGeometry, onGeometryReady]);

  useFrame((state, delta) => {
    if (animate && meshRef.current) {
      meshRef.current.rotation.z += delta * speed;
    }
  });

  return (
    <mesh ref={meshRef} geometry={geometry} rotation={rotation} position={position}>
      <meshStandardMaterial
        color={color}
        metalness={0.6}
        roughness={0.3}
        emissive={color}
        emissiveIntensity={0.1}
      />
    </mesh>
  );
};

// Isolated component for camera controls to prevent full app re-renders
const CameraOverlay = ({ controlsRef, onAnimateToggle, animate }) => {
  const [zoomLevel, setZoomLevel] = useState(1);

  const handleZoom = (direction) => {
    if (!controlsRef.current) return;
    const factor = direction === 'in' ? 0.9 : 1.1;
    const camera = controlsRef.current.object;
    camera.position.multiplyScalar(factor);
    controlsRef.current.update();
  };

  const handleRotate = (direction) => {
    if (!controlsRef.current) return;

    const angle = Math.PI / 16;
    const camera = controlsRef.current.object;
    const offset = new THREE.Vector3();
    offset.copy(camera.position).sub(controlsRef.current.target);

    const spherical = new THREE.Spherical();
    spherical.setFromVector3(offset);

    switch (direction) {
      case 'left':
        spherical.theta += angle;
        break;
      case 'right':
        spherical.theta -= angle;
        break;
      case 'up':
        spherical.phi -= angle;
        break;
      case 'down':
        spherical.phi += angle;
        break;
      default:
        break;
    }

    spherical.makeSafe();
    offset.setFromSpherical(spherical);
    camera.position.copy(controlsRef.current.target).add(offset);
    controlsRef.current.update();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!controlsRef.current) return;
      switch (e.key) {
        case 'ArrowLeft':
          handleRotate('left');
          break;
        case 'ArrowRight':
          handleRotate('right');
          break;
        case 'ArrowUp':
          handleRotate('up');
          break;
        case 'ArrowDown':
          handleRotate('down');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [controlsRef]);

  useEffect(() => {
    if (!controlsRef.current) return;

    const handleChange = () => {
      const distance = controlsRef.current.object.position.length();
      const initialDist = 70;
      setZoomLevel(initialDist / distance);
    };

    const controls = controlsRef.current;
    controls.addEventListener('change', handleChange);
    return () => controls.removeEventListener('change', handleChange);
  }, [controlsRef]);

  return (
    <div className="camera-controls">
      <div className="zoom-indicator">
        Zoom: {zoomLevel.toFixed(1)}x
      </div>

      <div className="control-card">
        <div className="control-label">Zoom</div>
        <div className="zoom-controls">
          <button className="control-btn" onClick={() => handleZoom('in')}>+</button>
          <button className="control-btn" onClick={() => handleZoom('out')}>-</button>
        </div>
      </div>

      <div className="control-card">
        <div className="control-label">Rotate</div>
        <div className="rotation-grid">
          <div />
          <button className="control-btn sm" onClick={() => handleRotate('up')}>↑</button>
          <div />
          <button className="control-btn sm" onClick={() => handleRotate('left')}>←</button>
          <button className="control-btn sm" onClick={() => handleRotate('down')}>↓</button>
          <button className="control-btn sm" onClick={() => handleRotate('right')}>→</button>
        </div>
      </div>

      <div className="control-card">
        <button
          className={`control-btn ${animate ? 'active' : ''}`}
          onClick={onAnimateToggle}
          style={{ width: '100%', padding: '0 10px', background: animate ? '#15803d' : '#6b7280', fontSize: '0.8rem' }}
        >
          {animate ? 'Stop Motion' : 'Start Motion'}
        </button>
      </div>
    </div>
  );
};

// Handles synchronized rack translation with pinion rotation (rack & pinion)
function RackMesher({ rackRef, pinionRef, rackBounds, pinionPitchRadius, rackPitch, animate, rackDirectionRef }) {
  // Angular speed in rad/sec; negative so initial motion goes right-to-left visually
  const pinionSpeed = -0.5;

  useFrame((_, delta) => {
    if (!animate) return;
    const pinion = pinionRef.current;
    const rack = rackRef.current;
    if (!pinion || !rack) return;

    // Apply rotation with direction
    const deltaRot = delta * pinionSpeed * rackDirectionRef.current;
    pinion.rotation.z += deltaRot;

    // Translate rack to maintain rolling contact: s = r * theta
    const deltaX = deltaRot * pinionPitchRadius;
    // Invert sign vs rotation so rack moves correctly relative to pinion
    rack.position.x += deltaX;

    const leftEdge = rack.position.x + rackBounds.minX;
    const rightEdge = rack.position.x + rackBounds.maxX;

    const buffer = rackPitch / 2;
    const leftLimit = buffer;
    const rightLimit = -buffer;

    if (leftEdge > leftLimit || rightEdge < rightLimit) {
      rackDirectionRef.current *= -1;
      // Clamp so contact point stays within usable span
      if (leftEdge > leftLimit) {
        rack.position.x -= (leftEdge - leftLimit);
      } else if (rightEdge < rightLimit) {
        rack.position.x += (rightLimit - rightEdge);
      }
    }
  });

  return null;
}

function GearViewer() {
  const navigate = useNavigate();
  // Configurator state
  const [params, setParams] = useState({
    teeth: 20,
    module: 1.0,
    pressureAngle: 20.0,
    faceWidth: 5.0,
    boreDiameter: 5.0,
    helixAngle: 0.0,
    hubDiameter: 0.0,
    hubLength: 0.0,
    keyway: false,
    material: 'Steel',
    backlash: 0.0,
    quality: 'Standard',
    units: 'Metric',
    type: 'external',
    showMeshingGear: false,
    ratio: 1.0,
    pinionTeeth: 16,
    animate: false
  });

  const [derived, setDerived] = useState({
    pitchDiameter: 0,
    outsideDiameter: 0,
    baseDiameter: 0,
    rootDiameter: 0,
    circularPitch: 0,
    followerTeeth: 0,
    centerDistance: 0
  });
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const [mainStlUrl, setMainStlUrl] = useState("https://industriquote.com/api/serve-default");
  const [meshStlUrl, setMeshStlUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rackBounds, setRackBounds] = useState(null);
  const controlsRef = useRef();
  const pinionPitchRadius = params.module * (params.pinionTeeth || 0) / 2;
  const rackPitch = Math.PI * params.module;
  const fallbackRackBounds = {
    minX: -rackPitch / 2,
    maxX: params.teeth * rackPitch - rackPitch / 2
  };
  const activeRackBounds = rackBounds || fallbackRackBounds;
  const rackGapCenterOffset = rackPitch / 2;
  const rackOffsetX = params.type === 'rack' ? -rackGapCenterOffset : 0;
  const rackRef = useRef();
  const pinionRef = useRef();
  const rackDirectionRef = useRef(1);

  useEffect(() => {
    setRackBounds(null);
  }, [meshStlUrl, params.type]);

  const setGearType = (type) => {
    const isRing = type === 'internal';
    const isRack = type === 'rack';

    if (isRing) {
      setParams(prev => ({
        ...prev,
        type: 'internal',
        teeth: 25,
        module: 1.5,
        boreDiameter: 0,
        helixAngle: 0,
        hubDiameter: 0,
        hubLength: 0,
        keyway: false,
        showMeshingGear: false
      }));
      setHasPendingChanges(true);
      setMainStlUrl("https://industriquote.com/api/serve-default?internal=true");
      setMeshStlUrl(null);
    } else if (isRack) {
      setParams(prev => ({
        ...prev,
        type: 'rack',
        teeth: 20,
        module: 1.5,
        boreDiameter: 0,
        helixAngle: 0,
        hubDiameter: 0,
        hubLength: 0,
        keyway: false,
        showMeshingGear: true,
        pinionTeeth: 16
      }));
      setHasPendingChanges(true);
      setMeshStlUrl(null);
    } else {
      setParams(prev => ({
        ...prev,
        type: 'external',
        teeth: 20,
        module: 1.5,
        boreDiameter: 8,
        helixAngle: 0,
        hubDiameter: 15,
        hubLength: 5,
        keyway: false
      }));
      setHasPendingChanges(true);
      setMainStlUrl("https://industriquote.com/api/serve-default?internal=false");
      setMeshStlUrl(null);
    }
  };

  useEffect(() => {
    // Calculate derived values
    const m = params.module;
    const z1 = params.teeth;
    const ratio = params.ratio || 1.0;
    const z2 = Math.round(z1 * ratio);
    const alpha = (params.pressureAngle * Math.PI) / 180;

    const pd = m * z1;
    const od = pd + 2 * m;
    const bd = pd * Math.cos(alpha);
    const rd = pd - 2.5 * m;
    const cp = Math.PI * m;

    const cd = (m * z1 + m * z2) / 2.0;

    setDerived({
      pitchDiameter: pd.toFixed(2),
      outsideDiameter: od.toFixed(2),
      baseDiameter: bd.toFixed(2),
      rootDiameter: rd.toFixed(2),
      circularPitch: cp.toFixed(3),
      followerTeeth: z2,
      centerDistance: cd.toFixed(2)
    });
  }, [params.module, params.teeth, params.pressureAngle, params.ratio]);

  const fetchGear = async () => {
    setLoading(true);
    setParams(prev => ({ ...prev, animate: false }));
    setHasPendingChanges(false);
    rackDirectionRef.current = 1;
    try {
      const commonQs = {
        teeth: params.teeth,
        module: params.module,
        pressure_angle: params.pressureAngle,
        face_width: params.faceWidth,
        bore_diameter: params.boreDiameter,
        helix_angle: params.helixAngle,
        hub_diameter: params.hubDiameter,
        hub_length: params.hubLength,
        keyway: params.keyway,
        type: params.type,
        mesh: params.showMeshingGear,
        ratio: params.ratio || 1.0
      };

      // Fetch Main Gear
      const mainQs = new URLSearchParams({
        ...commonQs,
        part: 'main',
        ...(params.type === 'rack' ? { pinion_teeth: params.pinionTeeth } : {})
      }).toString();
      const mainRes = await fetch(`https://industriquote.com/api/serve-gear?${mainQs}`);
      if (!mainRes.ok) throw new Error("Failed to fetch main gear");

      const mainBlob = await mainRes.blob();
      const mainUrl = URL.createObjectURL(mainBlob);

      if (mainStlUrl && mainStlUrl.startsWith('blob:')) URL.revokeObjectURL(mainStlUrl);
      setMainStlUrl(mainUrl);

      // Fetch Mesh Gear if enabled
      if (params.showMeshingGear && params.type === 'external') {
        const meshQs = new URLSearchParams({ ...commonQs, part: 'mesh' }).toString();
        const meshRes = await fetch(`https://industriquote.com/api/serve-gear?${meshQs}`);
        if (!meshRes.ok) throw new Error("Failed to fetch mesh gear");

        const meshBlob = await meshRes.blob();
        const meshUrl = URL.createObjectURL(meshBlob);

        if (meshStlUrl && meshStlUrl.startsWith('blob:')) URL.revokeObjectURL(meshStlUrl);
        setMeshStlUrl(meshUrl);
      } else if (params.showMeshingGear && params.type === 'rack') {
        const pinionQs = new URLSearchParams({
          ...commonQs,
          part: 'pinion',
          pinion_teeth: params.pinionTeeth
        }).toString();
        const pinionRes = await fetch(`https://industriquote.com/api/serve-gear?${pinionQs}`);
        if (!pinionRes.ok) throw new Error("Failed to fetch rack pinion");

        const pinionBlob = await pinionRes.blob();
        const pinionUrl = URL.createObjectURL(pinionBlob);

        if (meshStlUrl && meshStlUrl.startsWith('blob:')) URL.revokeObjectURL(meshStlUrl);
        setMeshStlUrl(pinionUrl);
      } else {
        setMeshStlUrl(null);
      }

    } catch (err) {
      console.error(err);
      alert("Error generating gear connecting to API.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = value;
    if (type === 'checkbox') finalValue = checked;
    else if (type === 'range' || (type === 'number' && name !== 'type' && name !== 'material' && name !== 'quality')) {
      finalValue = parseFloat(value);
    }

    setParams(prev => ({
      ...prev,
      [name]: finalValue
    }));
    setHasPendingChanges(true);

    // Get positioning relative to the viewport for a fixed tooltip
    const rect = e.target.getBoundingClientRect();
    setTooltipPos({
      y: rect.top + (rect.height / 2),
      x: rect.right + 15
    });
  };

  const handleDownload = () => {
    if (!mainStlUrl) return;
    const a = document.createElement('a');
    a.href = mainStlUrl;
    a.download = `gear_${params.teeth}t_${params.module}m.stl`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="App">
      <div className="sidebar">
        <div>
          <button
            className="control-btn"
            style={{ width: '100%', padding: '0 15px', marginBottom: '1.5rem', fontSize: '0.75rem', background: '#6b7280', fontFamily: 'inherit' }}
            onClick={() => navigate('/')}
          >
            Back to Hub
          </button>
          <h1>Parametric Gear</h1>
          <p>Generate exact involute 3D spur gears instantly with CadQuery.</p>
        </div>

        <div className="type-selector" style={{ display: 'flex', background: '#f0f2f5', padding: '3px', borderRadius: '3px', marginBottom: '1rem', border: '1px solid #d1d5db' }}>
          {['external', 'internal', 'rack'].map(t => (
            <button
              key={t}
              onClick={() => setGearType(t)}
              style={{
                flex: 1, padding: '0.4rem', border: 'none', borderRadius: '2px', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem',
                background: params.type === t ? '#1d4ed8' : 'transparent',
                color: params.type === t ? '#ffffff' : '#6b7280',
                transition: 'all 0.2s',
                textTransform: 'capitalize',
                fontFamily: 'inherit'
              }}>
              {t}
            </button>
          ))}
        </div>

        <div className="section-title">Geometry</div>
        <div className="control-row">
          <div className="control-group">
            <label>Teeth <span>{params.teeth}</span></label>
            <input type="range" name="teeth" min="6" max="25" step="1" value={params.teeth} onChange={handleChange} className="range-slider" />
          </div>
          <div className="control-group">
            <label>Module <span>{params.module}</span></label>
            <input type="range" name="module" min="0.5" max="5.0" step="0.1" value={params.module} onChange={handleChange} className="range-slider" />
          </div>
        </div>

        <div className="control-row">
          <div className="control-group">
            <label>PA <span>{params.pressureAngle}°</span></label>
            <input type="range" name="pressureAngle" min="10" max="30" step="0.5" value={params.pressureAngle} onChange={handleChange} className="range-slider" />
          </div>
          <div className="control-group" style={{ opacity: params.type === 'rack' ? 0.3 : 1 }}>
            <label>Helix <span>{params.helixAngle}°</span></label>
            <input
              type="range" name="helixAngle" min="0" max="45" step="1"
              value={params.helixAngle} onChange={handleChange} className="range-slider"
              disabled={params.type === 'rack'}
            />
          </div>
        </div>

        <div className="section-title">Dimensions</div>
        <div className="control-row">
          <div className="control-group">
            <label>Width <span>{params.units === 'Metric' ? `${params.faceWidth}mm` : `${(params.faceWidth / 25.4).toFixed(3)}in`}</span></label>
            <input type="range" name="faceWidth" min="1" max="50" step="1" value={params.faceWidth} onChange={handleChange} className="range-slider" />
          </div>
          {params.type === 'external' && (
            <div className="control-group">
              <label>Bore <span>{params.units === 'Metric' ? `${params.boreDiameter}mm` : `${(params.boreDiameter / 25.4).toFixed(3)}in`}</span></label>
              <input type="range" name="boreDiameter" min="0" max={params.teeth * params.module - 2} step="0.5" value={params.boreDiameter} onChange={handleChange} className="range-slider" />
            </div>
          )}
        </div>

        {params.type === 'external' && (
          <div className="control-row">
            <div className="control-group">
              <label>Hub Dia <span>{params.units === 'Metric' ? `${params.hubDiameter}mm` : `${(params.hubDiameter / 25.4).toFixed(3)}in`}</span></label>
              <input type="range" name="hubDiameter" min="0" max={params.teeth * params.module - 2} step="1" value={params.hubDiameter} onChange={handleChange} className="range-slider" />
            </div>
            <div className="control-group">
              <label>Hub Len <span>{params.units === 'Metric' ? `${params.hubLength}mm` : `${(params.hubLength / 25.4).toFixed(3)}in`}</span></label>
              <input type="range" name="hubLength" min="0" max="50" step="1" value={params.hubLength} onChange={handleChange} className="range-slider" />
            </div>
          </div>
        )}

        <div className="section-title">Engineering</div>
        <div className="control-row" style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <label style={{ margin: 0, fontSize: '0.8rem' }}>Material</label>
          <select name="material" value={params.material} onChange={handleChange} className="select-input" style={{ width: '60%' }}>
            {['Steel', 'Aluminum', 'Plastic', 'Brass'].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        <div className="control-group">
          <label>Backlash <span>{params.units === 'Metric' ? `${params.backlash}mm` : `${(params.backlash / 25.4).toFixed(4)}in`}</span></label>
          <input type="range" name="backlash" min="0" max="1" step="0.05" value={params.backlash} onChange={handleChange} className="range-slider" />
        </div>

        <div className="section-title">Tools & Meshing</div>
        <div className="control-row" style={{ alignItems: 'center', gap: '8px', marginBottom: '0.8rem' }}>
          <input type="checkbox" id="unitsToggle" name="units" checked={params.units === 'Inches'} onChange={(e) => setParams(p => ({ ...p, units: e.target.checked ? 'Inches' : 'Metric' }))} style={{ cursor: 'pointer' }} />
          <label htmlFor="unitsToggle" style={{ cursor: 'pointer', margin: 0, fontSize: '0.8rem' }}>Use Imperial (inches)?</label>
        </div>

        {params.type === 'external' && (
          <>
            <div className="control-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
              <input type="checkbox" id="meshToggle" name="showMeshingGear" checked={params.showMeshingGear} onChange={handleChange} style={{ cursor: 'pointer' }} />
              <label htmlFor="meshToggle" style={{ cursor: 'pointer', margin: 0, fontSize: '0.8rem' }}>Add Meshing Pinion?</label>
            </div>
            {params.showMeshingGear && (
              <div className="control-group">
                <label>Ratio <span>{params.ratio || 1.0} : 1</span></label>
                <input type="range" name="ratio" min="0.1" max="5.0" step="0.1" value={params.ratio || 1.0} onChange={handleChange} className="range-slider" />
              </div>
            )}
          </>
        )}

        {params.type === 'rack' && (
          <>
            <div className="control-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
              <input type="checkbox" id="rackMeshToggle" name="showMeshingGear" checked={params.showMeshingGear} onChange={handleChange} style={{ cursor: 'pointer' }} />
              <label htmlFor="rackMeshToggle" style={{ cursor: 'pointer', margin: 0, fontSize: '0.8rem' }}>Add Meshing Pinion?</label>
            </div>
            {params.showMeshingGear && (
              <div className="control-group">
                <label>Pinion Teeth <span>{params.pinionTeeth}</span></label>
                <input type="range" name="pinionTeeth" min="6" max="25" step="1" value={params.pinionTeeth} onChange={handleChange} className="range-slider" />
              </div>
            )}
          </>
        )}

        <div className="derived-outputs">
          <div className="derived-row">
            <span>Pitch Dia:</span>
            <strong>{params.units === 'Metric' ? derived.pitchDiameter : (derived.pitchDiameter / 25.4).toFixed(3)} {params.units === 'Metric' ? 'mm' : 'in'}</strong>
          </div>
          <div className="derived-row">
            <span>Outside Dia:</span>
            <strong>{params.units === 'Metric' ? derived.outsideDiameter : (derived.outsideDiameter / 25.4).toFixed(3)} {params.units === 'Metric' ? 'mm' : 'in'}</strong>
          </div>
          {params.showMeshingGear && (
            <>
              <div className="derived-row" style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #d1d5db' }}>
                <span>Follower Teeth:</span>
                <strong>{derived.followerTeeth} T</strong>
              </div>
              <div className="derived-row">
                <span>Center Dist:</span>
                <strong>{params.units === 'Metric' ? derived.centerDistance : (derived.centerDistance / 25.4).toFixed(3)} {params.units === 'Metric' ? 'mm' : 'in'}</strong>
              </div>
            </>
          )}
        </div>

        <div
          className="button-group"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            marginTop: '1rem'
          }}
        >
          <button
            className={`button-primary ${hasPendingChanges ? 'button-pulse' : ''}`}
            onClick={fetchGear}
            style={{ padding: '0.6rem', fontSize: '0.9rem' }}
          >
            {loading ? "Building..." : "Generate"}
          </button>

          <button
            className="button-primary"
            onClick={handleDownload}
            disabled={!mainStlUrl}
            style={{
              padding: '0.6rem',
              fontSize: '0.9rem',
              background: mainStlUrl ? '#15803d' : '#d1d5db',
              color: mainStlUrl ? '#fff' : '#9ca3af'
            }}
          >
            Export STL
          </button>
        </div>
      </div>

      <div className="canvas-container">
        {loading && <div className="loading-overlay">Generating Involute Profile...</div>}

        <CameraOverlay
          controlsRef={controlsRef}
          animate={params.animate}
          onAnimateToggle={() => setParams(p => ({ ...p, animate: !p.animate }))}
        />

        <Canvas dpr={1} camera={{ position: [0, -50, 50], fov: 50 }}>
          <color attach="background" args={['#dde3ea']} />
          <ambientLight intensity={0.8} />
          <spotLight position={[100, 100, 100]} angle={0.25} penumbra={1} intensity={2} />
          <pointLight position={[-100, -100, -100]} intensity={1} />
          <directionalLight position={[0, 50, 0]} intensity={1.5} />

          <Suspense fallback={null}>
            <Environment preset="city" />
            <group position={params.type === 'external' && params.showMeshingGear ? [-parseFloat(derived.centerDistance) / 2, 0, 0] : [0, 0, 0]}>
              {mainStlUrl && (
                <GearModel
                  key={mainStlUrl}
                  url={mainStlUrl}
                  animate={params.type === 'rack' ? false : params.animate}
                  speed={0.5}
                  color="#94a3b8"
                  position={params.type === 'rack' ? [rackOffsetX, 0, 0] : [0, 0, 0]}
                  centerGeometry={params.type !== 'rack'}
                  meshRefExternal={params.type === 'rack' ? rackRef : null}
                  onGeometryReady={params.type === 'rack' ? setRackBounds : undefined}
                />
              )}
              {params.type === 'external' && params.showMeshingGear && meshStlUrl && (
                <GearModel
                  key={meshStlUrl}
                  url={meshStlUrl}
                  position={[parseFloat(derived.centerDistance), 0, 0]}
                  rotation={[0, 0, Math.PI / derived.followerTeeth]}
                  animate={params.animate}
                  speed={-0.5 * (params.teeth / derived.followerTeeth)}
                  color="#eab308"
                />
              )}
              {params.type === 'rack' && params.showMeshingGear && meshStlUrl && (
                <GearModel
                  key={meshStlUrl}
                  url={meshStlUrl}
                  position={[0, pinionPitchRadius, params.faceWidth / 2]}
                  rotation={[0, 0, 0]}
                  animate={false}
                  speed={0}
                  color="#eab308"
                  meshRefExternal={pinionRef}
                />
              )}
            </group>
            {params.type === 'rack' && params.showMeshingGear && (
              <RackMesher
                rackRef={rackRef}
                pinionRef={pinionRef}
                rackBounds={activeRackBounds}
                pinionPitchRadius={pinionPitchRadius}
                rackPitch={rackPitch}
                animate={params.animate}
                rackDirectionRef={rackDirectionRef}
              />
            )}
          </Suspense>

          <OrbitControls
            ref={controlsRef}
            makeDefault
            enablePan={true}
            enableZoom={true}
          />
        </Canvas>
      </div>

      {hasPendingChanges && (
        <div
          className="notification tooltip"
          onClick={fetchGear}
          title="Click to apply changes"
          style={{
            left: `${tooltipPos.x}px`,
            top: `${tooltipPos.y}px`,
            position: 'fixed',
            transform: 'translateY(-50%)'
          }}
        >
          <span>Click <strong>Generate</strong> to apply changes</span>
        </div>
      )}
    </div>
  );
}

export default GearViewer;
